import {
  BaseWidget,
  BaseWidgetProps,
  Config,
  FactoryWithContext,
} from '@react-awesome-query-builder/mui';

import {
  AutocompleteResultWithTooltip,
  isTemplatePlaceholder,
  MailingStatus,
  TemplatePlaceholder,
} from '@/types';

import { AccordionField } from '@components/DrawerAccordion';
import { Search } from '@mui/icons-material';
import { Box, FormControl, MenuItem, Typography } from '@mui/material';
import { useLookupMailing } from '@/criteriaLookups';
import { SearchTemplatePageContext } from '@/context/SearchTemplateContext';
import { useContext } from 'react';
import { MappingError } from '@/pages/AdvancedSearchListCreatorPage/qbRulePropertiesMapping/FilterMappers';
import MissingFilterValueWarning from './MissingFilterValueWarning';
import TemplateCapableFetchAutocomplete from './templateCapableComponents/TemplateCapableFetchAutocomplete';
import TemplateCapableSelect from './templateCapableComponents/TemplateCapableSelectV2';

type MailingWidgetValue = {
  mailings: AutocompleteResultWithTooltip[] | TemplatePlaceholder;
  mailingStatus: MailingStatus | TemplatePlaceholder;
  errors: MappingError[];
};

type CombinedProps = BaseWidgetProps<Config, MailingWidgetValue>;

export const MailingWidgetFactory: FactoryWithContext<CombinedProps> = ({
  value,
  setValue,
}: CombinedProps) => {
  const inSearchTemplate = useContext(SearchTemplatePageContext);

  const listValuesForDisplay = [
    { title: 'Delivered', value: 'delivered' },
    { title: 'Unopened', value: 'unopened' },
    { title: 'Opened', value: 'opened' },
  ];

  const handleChangeMailing = (newValue: AutocompleteResultWithTooltip[] | TemplatePlaceholder) => {
    // setValue does not support updater functions like useState hook setter
    // function does, so we have to reference the old value directly.
    setValue({
      ...(value ?? { mailingStatus: 'delivered' }),
      mailings: newValue,
      errors: [],
    });
  };

  const handleChangeStatus = (selectValue: TemplatePlaceholder | string) => {
    setValue({
      ...value,
      mailingStatus: selectValue,
      errors: [],
    } as MailingWidgetValue);
  };

  const lookup = useLookupMailing();
  const isMissingMailings = !!value?.errors?.find(({ field }) => field === 'mailings');

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: "column",
        gap: 3
      }}>
      <AccordionField title="Received mailing">
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "0.5rem"
          }}>
          <MissingFilterValueWarning isMissing={isMissingMailings} isRequired={false} />
          <TemplateCapableFetchAutocomplete
            lookup={lookup}
            minLength={3}
            label="Add a mailing"
            value={value?.mailings ?? []}
            onChange={handleChangeMailing}
            data-testid="getfiltermailings"
            loadingText="Looking for mailings..."
            noOptionsText="No mailings found"
            popupIcon={<Search />}
            disableIconFlip
            sx={{ width: '90ch' }}
            templateBuilderDisplayName="Mailing filter: Mailing"
            isOnTemplatePage={inSearchTemplate}
            error={isMissingMailings}
          />
        </Box>
      </AccordionField>

      <Box>
        <em>Note: Mailings older than 3 months will not have delivery data</em>
      </Box>

      <Box>
        <Typography>
          {isTemplatePlaceholder(value?.mailings) || (value?.mailings ?? []).length
            ? 'Options for the selected mailing(s)'
            : 'Select at least one received mailing to see more mailing options'}
        </Typography>
      </Box>
      {(isTemplatePlaceholder(value?.mailings) || (value?.mailings ?? []).length !== 0) && (
        <Box sx={{
          width: "30ch"
        }}>
          <Typography id="mailing-status-label">Mailing status</Typography>
          <FormControl fullWidth>
            <TemplateCapableSelect
              isOnTemplatePage={inSearchTemplate}
              labelId="mailing-status-label"
              value={value?.mailingStatus || listValuesForDisplay[0].value}
              onChange={handleChangeStatus}
              valueAfterRemovePlaceholder={listValuesForDisplay[0].value}
              templateBuilderDisplayName={'Mailing filter: Mailing status'}
              variant="filled"
            >
              {listValuesForDisplay.map((x) => (
                <MenuItem key={x.value} value={x.value}>
                  {x.title}
                </MenuItem>
              ))}
            </TemplateCapableSelect>
          </FormControl>
        </Box>
      )}
    </Box>
  );
};

export const mailingWidget: BaseWidget = {
  type: 'mailing',
  hideOperator: true,
  operatorInlineLabel: 'has',
  factory: MailingWidgetFactory,
};
